/**
 * Tax planning's front door: clients the firm's rules suggest, clients who asked in the portal,
 * starting a plan (a planning engagement with its fee and a linked plan), the firm's planning
 * settings, and the client's "Request tax planning" card.
 */
import { DEFAULT_PLANNING_SETTINGS, formatCents, PLANNING_TIER_LABELS, PLANNING_TIERS, type PlanningSettings, type PlanningTierKey } from "@clientflow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Check, ChevronDown, Lightbulb, MessageSquare, Sparkles } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { askText } from "@/components/dialogs";
import { Empty, ListSearch, Loading, Pager, useServerPaged } from "@/components/page";
import { MoneyInput } from "@/components/bookkeeping";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useMembers } from "@/lib/members";
import { hasPermission, type Me } from "@/lib/session";

const select = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

type Opportunity = { clientId: string; name: string; kind: string; tier: PlanningTierKey; included: boolean; reasons: Array<{ key: string; label: string; detail: string }> };
type PlanningRequest = { id: string; clientId: string; clientName: string; clientKind: string; note: string; createdAt: string; requestedBy: string | null };
export type StartTarget = { clientId: string; name: string; tier?: PlanningTierKey; included?: boolean; requestId?: string };

const refresh = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ predicate: (q) => /^\/api\/(tax-planning|tax-plans)/.test(String(q.queryKey[0])) });

/** The plan year to suggest: next year once the current one is nearly over. */
export function defaultPlanYear(today = new Date()) {
  return today.getMonth() >= 9 ? today.getFullYear() + 1 : today.getFullYear();
}

function useSettings(): PlanningSettings {
  const { data } = useQuery<{ taxPlanning?: PlanningSettings }>({ queryKey: ["/api/firm/settings"] });
  return data?.taxPlanning ?? DEFAULT_PLANNING_SETTINGS;
}

function feeLabel(cfg: PlanningSettings, tier: PlanningTierKey, included?: boolean) {
  if (included) return "Included with the CFO plan";
  const fee = cfg.tiers[tier].feeCents;
  return fee === null ? "Fee not set yet (Firm Settings › Services and workflows › Tax planning)" : formatCents(fee);
}

const STEPS: Array<[string, string]> = [
  ["Requested", "A suggestion, a client's portal request or a website inquiry. Choose Start planning."],
  ["Proposal & engagement letter", "The engagement opens at the tier's fee. Skipped when the plan is included with CFO."],
  ["Gathering information", "The planning questionnaire goes to the client; their answers ask for the documents the plan needs."],
  ["Analysis", "Build the current path and up to four scenarios from the strategy library."],
  ["Plan review", "Send the plan for review; another preparer approves it."],
  ["Plan presented", "Share the approved plan in the portal and hold the review meeting."],
  ["Implementation", "The client accepts; each strategy becomes a dated action item and firm task."],
  ["Complete", "After the mid-year check-in."],
];

/** Tax Planning › How tax planning works: the rationale, the steps and the rules, in one place. */
export function PlanningGuide() {
  const cfg = useSettings();
  const t = cfg.thresholds;
  const whole = (c: number) => formatCents(c).replace(/\.00$/, "");
  return (
    <details className="group mb-4 overflow-hidden rounded-lg border border-card-border bg-card">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 bg-secondary/50 px-5 py-3 [&::-webkit-details-marker]:hidden">
        <span className="min-w-0">
          <span className="block text-[15px] font-semibold">How tax planning works</span>
          <span className="block text-xs text-muted-foreground">Why it is a separate service, the eight steps, and the rules that suggest clients and date their tasks.</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition group-open:rotate-180" aria-hidden />
      </summary>
      <div className="grid gap-6 border-t border-border px-5 py-4 lg:grid-cols-2">
        <div className="min-w-0 space-y-4">
          <div>
            <h3 className="text-sm font-semibold">Rationale</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Preparation reports on a year that is over; planning changes the year that is still open. Planning is therefore its own engagement, with a fee, stages, tasks and requests, and the plan is linked to it. Plans start from the firm's suggestions, a client's request in the portal, or a website inquiry.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold">Rules</h3>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>
                Suggested when tax owed with the last return is {whole(t.balanceDueCents)} or more, business profit is {whole(t.businessProfitCents)} or more a year, revenue grew {t.incomeGrowthPct}% or more, an individual return was accepted in the last 45 days{cfg.includedWithCfo ? ", or the client has a CFO plan" : ""}.
              </li>
              <li>Clients with a plan for the year, planning under way or an open request are not suggested again.</li>
              <li>The fee comes from the tier in Firm Settings › Services and workflows › Tax planning{cfg.includedWithCfo ? "; CFO clients' planning is included" : ""}.</li>
              <li>Accepted strategies are dated: an S corporation election by March 15, SEP and HSA contributions by April 15 of the next year, other moves by December 31.</li>
            </ul>
          </div>
        </div>
        <ol className="min-w-0 space-y-2">
          {STEPS.map(([name, text], i) => (
            <li key={name} className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2 text-sm">
              <span className="tabular grid h-6 w-6 place-items-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{i + 1}</span>
              <span>
                <span className="font-medium">{name}.</span> <span className="text-muted-foreground">{text}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    </details>
  );
}

/** Opens a planning engagement and its plan, and can send the questionnaire. */
export function StartPlanningDialog({ target, onClose, me }: { target: StartTarget | null; onClose: () => void; me: Me }) {
  const qc = useQueryClient();
  const [, go] = useLocation();
  const cfg = useSettings();
  const members = useMembers();
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<{ id: string; displayName: string; kind?: string } | null>(null);
  const client = target ? { id: target.clientId, displayName: target.name } : picked;
  const [tier, setTier] = useState<PlanningTierKey>(target?.tier ?? "individual");
  const [year, setYear] = useState(String(defaultPlanYear()));
  const [assignee, setAssignee] = useState("");
  // The questionnaire goes once the client has agreed to the engagement, or now when it's included.
  const canSend = hasPermission(me, "requests.write");
  const [send, setSend] = useState(!!target?.included);
  const [sendError, setSendError] = useState<{ message: string; planId: string } | null>(null);
  const { data: found } = useQuery<{ items: Array<{ id: string; displayName: string; kind: string }> }>({ queryKey: [`/api/clients?module=tax&status=active&limit=20&q=${encodeURIComponent(q.trim())}`], enabled: !target && q.trim().length > 0 });
  const start = useMutation({
    meta: { inlineError: true },
    mutationFn: async () => {
      const r = await api.send<{ engagementId: string; planId: string; questionnaireTemplateId: string | null }>("POST", "/api/tax-planning/start", {
        clientId: client!.id,
        tier,
        year: Number(year),
        ...(target?.requestId ? { requestId: target.requestId } : {}),
        ...(assignee ? { assignedUserId: assignee } : {}),
      });
      let failed: string | null = null;
      if (canSend && send && r.questionnaireTemplateId) {
        // The plan exists either way; a questionnaire that can't be sent is reported, not fatal.
        try {
          await api.send("POST", "/api/organizers", { clientId: client!.id, engagementId: r.engagementId, templateId: r.questionnaireTemplateId, taxYear: Number(year) });
        } catch (e) {
          failed = errorMessage(e);
        }
      }
      return { ...r, failed };
    },
    onSuccess: (r) => {
      refresh(qc);
      if (r.failed) setSendError({ message: r.failed, planId: r.planId });
      else go(`/tax/planning/${r.planId}`);
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Start tax planning{target ? ` for ${target.name}` : ""}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (client) start.mutate();
          }}
        >
          {!target && (
            <div className="space-y-1">
              <Label htmlFor="sp-client">Client</Label>
              {picked ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-input px-3 py-2 text-sm">
                  <span className="truncate font-medium">{picked.displayName}</span>
                  <button type="button" className="text-xs text-primary hover:underline" onClick={() => setPicked(null)}>
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <Input id="sp-client" autoFocus placeholder="Search tax clients by name" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
                  {q.trim().length > 0 && (
                    <ul className="max-h-48 overflow-y-auto rounded-md border border-border text-sm">
                      {(found?.items ?? []).map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-muted"
                            onClick={() => {
                              setPicked(c);
                              setTier(c.kind === "business" ? "business_owner" : "individual");
                            }}
                          >
                            {c.displayName}
                          </button>
                        </li>
                      ))}
                      {found && !found.items.length && <li className="px-3 py-2 text-muted-foreground">No tax client matches.</li>}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium">Planning tier</legend>
            {PLANNING_TIERS.map((k) => (
              <label key={k} className="flex cursor-pointer items-start gap-3 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input type="radio" name="tier" className="mt-1" checked={tier === k} onChange={() => setTier(k)} />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <span className="text-sm font-medium">{PLANNING_TIER_LABELS[k].label}</span>
                    <span className="tabular text-xs text-muted-foreground">{feeLabel(cfg, k, target?.included)}</span>
                  </span>
                  <span className="block text-xs text-muted-foreground">{PLANNING_TIER_LABELS[k].description}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="sp-year">Plan year</Label>
              <Input id="sp-year" inputMode="numeric" value={year} onChange={(e) => setYear(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="sp-who">Planner</Label>
              <select id="sp-who" className={select} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                <option value="">Me</option>
                {members
                  .filter((m) => m.role !== "readonly")
                  .map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          {canSend && (
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={send} onChange={(e) => setSend(e.target.checked)} />
              <span>
                Send the tax planning questionnaire now
                <span className="block text-xs text-muted-foreground">
                  Goals, life and income changes, business plans and savings, and the documents the plan needs. {target?.included ? "Planning is included with this client's CFO plan, so it can go now." : "Usually sent once the engagement letter is signed; you can send it later from the client's Organizers."}
                </span>
              </span>
            </label>
          )}
          <p className="rounded-md bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">
            This opens a Tax Planning engagement (proposal, information gathering, analysis, review, presentation and implementation) and the plan itself.
          </p>
          {sendError && (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              <p>Planning was started, but the questionnaire wasn't sent: {sendError.message} Send it from the client's Organizers when you can.</p>
              <Button type="button" size="sm" onClick={() => go(`/tax/planning/${sendError.planId}`)}>
                Open the plan
              </Button>
            </div>
          )}
          {start.error && <p className="text-sm text-destructive">{errorMessage(start.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!client || start.isPending || !!sendError || !/^\d{4}$/.test(year)}>
              {start.isPending ? "Starting…" : "Start planning"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Clients the firm's rules suggest for planning, with why. */
export function PlanningOpportunities({ me }: { me: Me }) {
  const [term, setTerm] = useState("");
  const sp = useServerPaged(term);
  const qs = new URLSearchParams({ ...(term ? { q: term } : {}), offset: String(sp.offset), limit: String(sp.size) });
  const { data, isLoading } = useQuery<{ items: Opportunity[]; total: number; settings: PlanningSettings }>({ queryKey: [`/api/tax-planning/opportunities?${qs}`], placeholderData: (prev) => prev });
  const [target, setTarget] = useState<StartTarget | null>(null);
  const canStart = hasPermission(me, "engagements.write");
  const t = data?.settings.thresholds;
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          {t ? (
            <>
              Suggested when a client owed {formatCents(t.balanceDueCents).replace(/\.00$/, "")} or more with their last return, a business earns {formatCents(t.businessProfitCents).replace(/\.00$/, "")} or more a year, revenue grew {t.incomeGrowthPct}% or more, a return was just accepted, or a CFO plan includes planning.
            </>
          ) : (
            "Clients who would benefit from planning, from their returns and books."
          )}
        </p>
        <ListSearch value={term} onChange={setTerm} placeholder="Search suggested clients" className="shrink-0" />
      </div>
      {isLoading ? (
        <Loading />
      ) : !data?.items.length ? (
        <Empty>{term ? "No suggested client matches." : "No suggestions right now. Clients with a plan for this year, planning under way or an open request aren't listed."}</Empty>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-card-border bg-card">
          {data.items.map((o) => (
            <li key={o.clientId} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link href={`/clients/${o.clientId}?tab=tax`} className="font-medium hover:underline">
                    {o.name}
                  </Link>
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{PLANNING_TIER_LABELS[o.tier].label}</span>
                  {o.included && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200">Included with CFO</span>}
                </div>
                <ul className="mt-1 space-y-0.5">
                  {o.reasons.map((r) => (
                    <li key={r.key} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                      <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
                      <span>
                        <span className="font-medium text-foreground">{r.label}:</span> {r.detail}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              {canStart && (
                <Button size="sm" variant="outline" className="shrink-0 self-start sm:self-center" onClick={() => setTarget({ clientId: o.clientId, name: o.name, tier: o.tier, included: o.included })}>
                  Start planning
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {data && <Pager paged={sp.paged(data.total)} noun="clients" />}
      {target && <StartPlanningDialog target={target} me={me} onClose={() => setTarget(null)} />}
    </div>
  );
}

/** Clients who asked for planning in the portal. */
export function PlanningRequests({ me }: { me: Me }) {
  const qc = useQueryClient();
  const [term, setTerm] = useState("");
  const sp = useServerPaged(term);
  const qs = new URLSearchParams({ ...(term ? { q: term } : {}), offset: String(sp.offset), limit: String(sp.size) });
  const { data, isLoading } = useQuery<{ items: PlanningRequest[]; total: number }>({ queryKey: [`/api/tax-planning/requests?${qs}`], placeholderData: (prev) => prev });
  const [target, setTarget] = useState<StartTarget | null>(null);
  const decline = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => api.send("POST", `/api/tax-planning/requests/${id}/decline`, { note }),
    onSuccess: () => refresh(qc),
  });
  const canStart = hasPermission(me, "engagements.write");
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">Clients who asked for tax planning in their portal. Each request also made a task for the client's staff member.</p>
        <ListSearch value={term} onChange={setTerm} placeholder="Search requests" className="shrink-0" />
      </div>
      {isLoading ? (
        <Loading />
      ) : !data?.items.length ? (
        <Empty>{term ? "No request matches." : "No open requests. Clients can ask for tax planning from their portal home page."}</Empty>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-card-border bg-card">
          {data.items.map((r) => (
            <li key={r.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <Link href={`/clients/${r.clientId}?tab=tax`} className="font-medium hover:underline">
                  {r.clientName}
                </Link>
                <p className="text-xs text-muted-foreground">
                  Asked {formatDate(r.createdAt)}
                  {r.requestedBy ? ` by ${r.requestedBy}` : ""}
                </p>
                {r.note && (
                  <p className="mt-1 flex items-start gap-1.5 text-sm">
                    <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="break-words">{r.note}</span>
                  </p>
                )}
              </div>
              {canStart && (
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={decline.isPending}
                    onClick={async () => {
                      const note = await askText({ title: `Close ${r.clientName}'s request without planning?`, label: "Note for the file (optional)", confirmLabel: "Close request", required: false });
                      if (note !== null) decline.mutate({ id: r.id, note });
                    }}
                  >
                    Close
                  </Button>
                  <Button size="sm" onClick={() => setTarget({ clientId: r.clientId, name: r.clientName, tier: r.clientKind === "business" ? "business_owner" : "individual", requestId: r.id })}>
                    Start planning
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {data && <Pager paged={sp.paged(data.total)} noun="requests" />}
      {target && <StartPlanningDialog target={target} me={me} onClose={() => setTarget(null)} />}
    </div>
  );
}

/** Firm Settings › Services and workflows › Tax planning: the tier fees, whether CFO plans include it, and the suggestion rules. */
export function PlanningSettingsPanel({ me }: { me: Me }) {
  const qc = useQueryClient();
  const saved = useSettings();
  const canEdit = hasPermission(me, "firm.manage");
  const [form, setForm] = useState<PlanningSettings | null>(null);
  const v = form ?? saved;
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("PATCH", "/api/firm/settings", { taxPlanning: v }),
    onSuccess: () => {
      setForm(null);
      qc.invalidateQueries({ queryKey: ["/api/firm/settings"] });
      refresh(qc);
    },
  });
  const set = (p: Partial<PlanningSettings>) => setForm({ ...v, ...p });
  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <section className="overflow-hidden rounded-lg border border-card-border bg-card">
        <div className="border-b border-border bg-secondary/50 px-5 py-3">
          <h2 className="text-[15px] font-semibold">Planning fees</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">The fee quoted when planning starts, by tier. Leave a fee blank until your pricing is set; the engagement is then opened without a quoted fee.</p>
        </div>
        <ul className="divide-y divide-border">
          {PLANNING_TIERS.map((k) => {
            const fee = v.tiers[k].feeCents;
            return (
              <li key={k} className="grid gap-2 px-5 py-3 sm:grid-cols-[1fr_12rem] sm:items-center">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{PLANNING_TIER_LABELS[k].label}</p>
                  <p className="text-xs text-muted-foreground">{PLANNING_TIER_LABELS[k].description}</p>
                </div>
                <div className="flex items-center gap-2">
                  {fee === null ? (
                    <Button type="button" size="sm" variant="outline" className="w-full" disabled={!canEdit} onClick={() => set({ tiers: { ...v.tiers, [k]: { feeCents: 0 } } })}>
                      Set a fee
                    </Button>
                  ) : (
                    <>
                      <MoneyInput label={`${PLANNING_TIER_LABELS[k].label} fee`} disabled={!canEdit} value={fee} onChange={(c) => set({ tiers: { ...v.tiers, [k]: { feeCents: c } } })} />
                      <button type="button" className="shrink-0 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50" disabled={!canEdit} onClick={() => set({ tiers: { ...v.tiers, [k]: { feeCents: null } } })}>
                        Clear
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        <label className="flex items-start gap-3 border-t border-border px-5 py-3">
          <input type="checkbox" className="mt-0.5 h-4 w-4" disabled={!canEdit} checked={v.includedWithCfo} onChange={(e) => set({ includedWithCfo: e.target.checked })} />
          <span>
            <span className="block text-sm font-medium">Include a plan each year with CFO and advisory</span>
            <span className="block text-xs text-muted-foreground">Those clients are suggested every year, and their planning engagement has no fee and skips the proposal step.</span>
          </span>
        </label>
      </section>
      <section className="overflow-hidden rounded-lg border border-card-border bg-card">
        <div className="border-b border-border bg-secondary/50 px-5 py-3">
          <h2 className="text-[15px] font-semibold">When to suggest planning</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">Tax Planning › Suggested lists clients who meet any of these. Clients who already have a plan for the year, planning under way or an open request aren't listed.</p>
        </div>
        <div className="grid gap-4 px-5 py-4 sm:grid-cols-3">
          <div className="space-y-1">
            <Label>Tax owed with the last return of at least</Label>
            <MoneyInput label="Balance due threshold" disabled={!canEdit} value={v.thresholds.balanceDueCents} onChange={(c) => set({ thresholds: { ...v.thresholds, balanceDueCents: c } })} />
          </div>
          <div className="space-y-1">
            <Label>Business profit a year of at least</Label>
            <MoneyInput label="Business profit threshold" disabled={!canEdit} value={v.thresholds.businessProfitCents} onChange={(c) => set({ thresholds: { ...v.thresholds, businessProfitCents: c } })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ps-growth">Revenue growth of at least (%)</Label>
            <Input id="ps-growth" inputMode="decimal" className="h-9 text-right tabular" disabled={!canEdit} value={String(v.thresholds.incomeGrowthPct)} onChange={(e) => set({ thresholds: { ...v.thresholds, incomeGrowthPct: Math.max(0, Math.min(1000, Number(e.target.value) || 0)) } })} />
          </div>
        </div>
        <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">Business profit and growth come from the monthly figures of clients whose books you keep. A client is also suggested when their individual return was accepted in the last 45 days.</p>
      </section>
      {canEdit && (
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={!form || save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          {form && (
            <Button type="button" variant="ghost" onClick={() => setForm(null)}>
              Discard changes
            </Button>
          )}
        </div>
      )}
      {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
    </form>
  );
}

type PortalStatus = { clientId: string; name: string; requested: boolean; underWay: boolean };

/** Portal home: the client can ask their firm for tax planning. */
export function PortalPlanningCard({ clientId }: { clientId?: string }) {
  const qc = useQueryClient();
  const { data } = useQuery<PortalStatus[]>({ queryKey: ["/api/portal/tax-planning/status"] });
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const list = (data ?? []).filter((x) => !clientId || x.clientId === clientId);
  const [pick, setPick] = useState("");
  const req = useMutation({
    meta: { inlineError: true },
    mutationFn: (id: string) => api.send("POST", "/api/portal/tax-planning/request", { clientId: id, note }),
    onSuccess: () => {
      setOpen(false);
      setNote("");
      qc.invalidateQueries({ queryKey: ["/api/portal/tax-planning/status"] });
    },
  });
  if (!list.length || list.every((x) => x.underWay)) return null;
  const waiting = list.filter((x) => !x.underWay);
  const asked = waiting.filter((x) => x.requested);
  const target = pick || waiting.find((x) => !x.requested)?.clientId || "";
  return (
    <section className="rounded-lg border border-card-border bg-card p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
            <Sparkles className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold">Tax planning</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">A tax plan reviews the year ahead (income, business, retirement and family changes) and sets out the steps that lower your tax, with the estimated saving for each.</p>
          </div>
        </div>
        {waiting.length > asked.length ? (
          <Button className="shrink-0" onClick={() => setOpen(true)}>
            Request tax planning
          </Button>
        ) : (
          <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-300">
            <Check className="h-4 w-4" aria-hidden /> Requested. Your firm will be in touch.
          </span>
        )}
      </div>
      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Request tax planning</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (target) req.mutate(target);
              }}
            >
              {waiting.filter((x) => !x.requested).length > 1 && (
                <div className="space-y-1">
                  <Label htmlFor="pp-for">For</Label>
                  <select id="pp-for" className={select} value={target} onChange={(e) => setPick(e.target.value)}>
                    {waiting
                      .filter((x) => !x.requested)
                      .map((x) => (
                        <option key={x.clientId} value={x.clientId}>
                          {x.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="pp-note">What would you like to plan for? (optional)</Label>
                <Textarea id="pp-note" rows={4} maxLength={2000} placeholder="For example: we're buying a rental, my business is growing, I'm retiring next year." value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                Your firm will contact you with a proposal, then send a short questionnaire.
              </p>
              {req.error && <p className="text-sm text-destructive">{errorMessage(req.error)}</p>}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!target || req.isPending}>
                  {req.isPending ? "Sending…" : "Send request"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
